import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ContextMenu } from './ContextMenu';
import { contextMenuService } from '../../services/context-menu-service';
import type { 
  ContextMenuContext, 
  ContextMenuItem, 
  ContextMenuPosition, 
  ContextMenuItemKey 
} from '../../types/context-menu';

interface ContextMenuState {
  items: ContextMenuItem[];
  position: ContextMenuPosition | null;
  context: ContextMenuContext | null;
}

interface ContextMenuContextType {
  showContextMenu: (
    event: React.MouseEvent,
    context: ContextMenuContext
  ) => Promise<void>;
  hideContextMenu: () => void;
  executeAction: (key: ContextMenuItemKey, subItemId?: ContextMenuItemKey) => Promise<void>;
}

const ContextMenuContext = createContext<ContextMenuContextType | null>(null);

interface ContextMenuProviderProps {
  children: ReactNode;
  onActionExecuted?: (key: ContextMenuItemKey, context: ContextMenuContext) => void;
}

export const ContextMenuProvider: React.FC<ContextMenuProviderProps> = ({
  children,
  onActionExecuted
}) => {
  const [menuState, setMenuState] = useState<ContextMenuState>({
    items: [],
    position: null,
    context: null
  });

  const showContextMenu = useCallback(async (
    event: React.MouseEvent,
    context: ContextMenuContext
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // Build menu items based on context
    const items = await contextMenuService.buildContextMenu(context);

    if (items.length === 0) {
      return;
    }

    // Calculate position
    const position: ContextMenuPosition = {
      x: event.clientX,
      y: event.clientY
    };

    // Adjust position if menu would go off-screen
    const menuWidth = 280; // Approximate max width
    const menuHeight = items.length * 36 + 16; // Approximate height

    if (position.x + menuWidth > window.innerWidth) {
      position.x = window.innerWidth - menuWidth - 8;
    }

    if (position.y + menuHeight > window.innerHeight) {
      position.y = window.innerHeight - menuHeight - 8;
    }

    setMenuState({
      items,
      position,
      context
    });
  }, []);

  const hideContextMenu = useCallback(() => {
    setMenuState({
      items: [],
      position: null,
      context: null
    });
  }, []);

  const executeAction = useCallback(async (
    key: ContextMenuItemKey,
    subItemId?: ContextMenuItemKey
  ) => {
    if (menuState.context) {
      await contextMenuService.executeAction(key, menuState.context, subItemId);
      
      // Notify parent component
      onActionExecuted?.(key, menuState.context);
    }
  }, [menuState.context, onActionExecuted]);

  const contextValue: ContextMenuContextType = {
    showContextMenu,
    hideContextMenu,
    executeAction
  };

  return (
    <ContextMenuContext.Provider value={contextValue}>
      {children}
      <ContextMenu
        items={menuState.items}
        position={menuState.position}
        context={menuState.context}
        onClose={hideContextMenu}
        onItemClick={executeAction}
      />
    </ContextMenuContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useContextMenu = (): ContextMenuContextType => {
  const context = useContext(ContextMenuContext);
  if (!context) {
    throw new Error('useContextMenu must be used within a ContextMenuProvider');
  }
  return context;
};